import { useState, useEffect, useRef } from 'react'
import { supabase } from './supabaseClient'

const NICHE_OPTIONS = [
  'B2B Technology', 'Healthcare & Medical', 'Finance & Fintech',
  'Marketing & Advertising', 'E-commerce & Retail', 'Education',
  'Legal', 'Real Estate', 'Travel & Lifestyle', 'Food & Wellness',
  'SaaS & Software', 'Nonprofit', 'Other',
]

export default function Profile({ session, theme, onNameChange, onAvatarChange, canEdit = true, isFounder = false }) {
  const [displayName, setDisplayName] = useState('')
  const [nameInput, setNameInput] = useState('')
  const [email, setEmail] = useState('')
  const [niche, setNiche] = useState('')
  const [nicheOther, setNicheOther] = useState('')
  const [targetIncome, setTargetIncome] = useState('')
  const [hourlyRate, setHourlyRate] = useState('')
  const [avatarUrl, setAvatarUrl] = useState('')
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [loading, setLoading] = useState(true)
  const fileInputRef = useRef(null)

  useEffect(() => {
    if (session?.user?.id) {
      setEmail(session.user.email || '')
      fetchProfile()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.user?.id])

  const fetchProfile = async () => {
    setLoading(true)
    const { data } = await supabase
      .from('profiles')
      .select('display_name, niche, niche_other, target_income, hourly_rate, avatar_url')
      .eq('user_id', session.user.id)
      .single()
    if (data) {
      if (data.display_name) { setDisplayName(data.display_name); setNameInput(data.display_name) }
      if (data.niche) setNiche(data.niche)
      if (data.niche_other) setNicheOther(data.niche_other)
      if (data.target_income != null) setTargetIncome(String(data.target_income))
      if (data.hourly_rate != null) setHourlyRate(String(data.hourly_rate))
      if (data.avatar_url) setAvatarUrl(data.avatar_url)
    }
    setLoading(false)
  }

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    setUploading(true)
    const filePath = `${session.user.id}/avatar`
    const { error: uploadError } = await supabase.storage
      .from('avatars')
      .upload(filePath, file, { upsert: true, contentType: file.type })
    if (!uploadError) {
      const { data: urlData } = supabase.storage.from('avatars').getPublicUrl(filePath)
      const url = urlData.publicUrl + '?t=' + Date.now()
      await supabase.from('profiles').upsert(
        { user_id: session.user.id, avatar_url: url },
        { onConflict: 'user_id' }
      )
      setAvatarUrl(url)
      if (onAvatarChange) onAvatarChange(url)
    }
    setUploading(false)
    // reset file input so same file can be re-uploaded if needed
    e.target.value = ''
  }

  const handleSave = async () => {
    const trimmed = nameInput.trim()
    if (!trimmed) return
    setSaving(true)
    const { error } = await supabase.from('profiles').upsert({
      user_id: session.user.id,
      display_name: trimmed,
      niche: niche || null,
      niche_other: niche === 'Other' ? (nicheOther || null) : null,
      target_income: targetIncome !== '' ? parseFloat(targetIncome) : null,
      hourly_rate: hourlyRate !== '' ? parseFloat(hourlyRate) : null,
    }, { onConflict: 'user_id' })
    if (!error) {
      setDisplayName(trimmed)
      onNameChange(trimmed)
      setSaved(true)
      setTimeout(() => setSaved(false), 2500)
    }
    setSaving(false)
  }

  const initial = displayName
    ? displayName[0].toUpperCase()
    : email ? email[0].toUpperCase() : '?'

  const canSave = nameInput.trim() && !saving

  const inputStyle = {
    width: '100%', background: theme.surfaceAlt, border: `1px solid ${theme.border}`,
    color: theme.text, padding: '10px 14px', fontSize: 14,
    fontFamily: "'Nunito Sans', sans-serif", boxSizing: 'border-box', outline: 'none',
  }

  const labelStyle = {
    fontSize: 10, color: theme.textSecondary, marginBottom: 6,
    letterSpacing: '0.1em', fontFamily: "'Open Sans', sans-serif", textTransform: 'uppercase',
  }

  return (
    <div style={{ maxWidth: 560 }}>
      <div style={{ background: theme.surface, border: `1px solid ${theme.border}`, padding: '28px 32px', marginBottom: 16 }}>
        <div style={{
          fontSize: 11, color: theme.accent, letterSpacing: '0.15em', textTransform: 'uppercase',
          fontFamily: "'Open Sans', sans-serif", marginBottom: 24, fontWeight: 700,
        }}>Your Profile</div>

        {/* Avatar upload */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 20, marginBottom: 28 }}>
          <div
            onClick={() => !uploading && fileInputRef.current?.click()}
            title="Click to upload a photo"
            style={{
              width: 64, height: 64, borderRadius: '50%',
              background: avatarUrl ? 'transparent' : theme.accent,
              border: `2px solid ${theme.border}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: uploading ? 'default' : 'pointer',
              overflow: 'hidden', flexShrink: 0,
              opacity: uploading ? 0.6 : 1,
              transition: 'opacity 0.15s',
            }}
          >
            {avatarUrl
              ? <img src={avatarUrl} alt="Avatar" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              : <span style={{
                  fontSize: 24, fontFamily: "'Cardo', serif", fontWeight: 700,
                  color: theme.mode === 'dark' ? '#000' : '#fff',
                }}>{initial}</span>
            }
          </div>
          <div>
            <div style={{ fontSize: 13, color: theme.text, fontFamily: "'Nunito Sans', sans-serif", marginBottom: 4 }}>
              {uploading ? 'Uploading...' : 'Profile Photo'}
            </div>
            <div style={{ fontSize: 11, color: theme.textMuted, fontFamily: "'Nunito Sans', sans-serif" }}>
              Click the circle to upload. JPG or PNG recommended.
            </div>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={handleAvatarUpload}
          />
        </div>

        {/* Display Name */}
        <div style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
            <div style={{ ...labelStyle, marginBottom: 0 }}>Display Name</div>
            {isFounder && (
              <span style={{
                background: '#d9ad2b', color: '#0a2342', borderRadius: 999,
                padding: '2px 10px', fontSize: 11, fontFamily: "'Open Sans', sans-serif",
                fontWeight: 700, letterSpacing: '0.06em',
              }}>Founder</span>
            )}
          </div>
          <input
            style={inputStyle}
            placeholder="e.g. Cody"
            value={nameInput}
            onChange={e => setNameInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && canEdit && handleSave()}
            disabled={loading || !canEdit}
          />
          <div style={{ fontSize: 11, color: theme.textMuted, marginTop: 6, fontFamily: "'Nunito Sans', sans-serif" }}>
            This is how we'll greet you on the dashboard.
          </div>
        </div>

        {/* Writing Niche */}
        <div style={{ marginBottom: niche === 'Other' ? 12 : 20 }}>
          <div style={labelStyle}>Writing Niche</div>
          <select
            style={{ ...inputStyle, appearance: 'none', cursor: canEdit ? 'pointer' : 'default' }}
            value={niche}
            onChange={e => setNiche(e.target.value)}
            disabled={!canEdit}
          >
            <option value="">Select your niche...</option>
            {NICHE_OPTIONS.map(n => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>

        {niche === 'Other' && (
          <div style={{ marginBottom: 20 }}>
            <div style={labelStyle}>Describe Your Niche</div>
            <input
              style={inputStyle}
              placeholder="e.g. Cybersecurity for SMBs"
              value={nicheOther}
              onChange={e => setNicheOther(e.target.value)}
            />
          </div>
        )}

        {/* Target Income + Hourly Rate */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
          <div>
            <div style={labelStyle}>Target Annual Income</div>
            <div style={{ position: 'relative' }}>
              <span style={{
                position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)',
                color: theme.textMuted, fontFamily: "'Nunito Sans', sans-serif", fontSize: 14,
                pointerEvents: 'none',
              }}>$</span>
              <input
                type="number"
                style={{ ...inputStyle, paddingLeft: 24 }}
                placeholder="e.g. 80000"
                value={targetIncome}
                onChange={e => setTargetIncome(e.target.value)}
                disabled={!canEdit}
              />
            </div>
          </div>
          <div>
            <div style={labelStyle}>Hourly Rate Target</div>
            <div style={{ position: 'relative' }}>
              <span style={{
                position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)',
                color: theme.textMuted, fontFamily: "'Nunito Sans', sans-serif", fontSize: 14,
                pointerEvents: 'none',
              }}>$</span>
              <input
                type="number"
                style={{ ...inputStyle, paddingLeft: 24 }}
                placeholder="e.g. 75"
                value={hourlyRate}
                onChange={e => setHourlyRate(e.target.value)}
                disabled={!canEdit}
              />
            </div>
          </div>
        </div>

        {/* Email (read-only) */}
        <div style={{ marginBottom: 24 }}>
          <div style={labelStyle}>Email</div>
          <div style={{
            padding: '10px 14px', fontSize: 14, color: theme.textMuted,
            fontFamily: "'Nunito Sans', sans-serif", background: theme.surfaceAlt,
            border: `1px solid ${theme.border}`,
          }}>
            {email}
          </div>
          <div style={{ fontSize: 11, color: theme.textMuted, marginTop: 6, fontFamily: "'Nunito Sans', sans-serif" }}>
            Your login email. Contact support to change it.
          </div>
        </div>

        {canEdit && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button
              onClick={handleSave}
              disabled={!canSave}
              style={{
                background: canSave ? theme.accent : theme.border,
                color: '#0a2342', border: 'none', padding: '9px 22px',
                fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase',
                fontFamily: "'Open Sans', sans-serif", fontWeight: 700,
                cursor: canSave ? 'pointer' : 'not-allowed',
              }}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
            {saved && (
              <div style={{ fontSize: 12, color: theme.statusGreenText, fontFamily: "'Nunito Sans', sans-serif" }}>
                ✓ Saved
              </div>
            )}
          </div>
        )}
      </div>

      {/* Account info */}
      <div style={{ background: theme.surface, border: `1px solid ${theme.border}`, padding: '28px 32px' }}>
        <div style={{
          fontSize: 11, color: theme.accent, letterSpacing: '0.15em', textTransform: 'uppercase',
          fontFamily: "'Open Sans', sans-serif", marginBottom: 20, fontWeight: 700,
        }}>Account</div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {[
            { label: 'Plan', value: 'Founding Member' },
            { label: 'Member Since', value: session?.user?.created_at ? new Date(session.user.created_at).toLocaleDateString('en-US', { month: 'long', year: 'numeric' }) : '—' },
          ].map((row, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: 14, borderBottom: i === 0 ? `1px solid ${theme.border}` : 'none' }}>
              <div style={{ fontSize: 12, color: theme.textMuted, fontFamily: "'Open Sans', sans-serif", letterSpacing: '0.08em', textTransform: 'uppercase' }}>{row.label}</div>
              <div style={{ fontSize: 13, color: theme.text, fontFamily: "'Nunito Sans', sans-serif", fontWeight: 600 }}>{row.value}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
